// =============================================================
// VARIANCE — The Executive Command Center
// File      : HelpSystem.tsx
// Location  : /home/Applications/variance/web/src/components/HelpSystem.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Context provider and UI components for Page Help, 
//             Context Help, and Field-level Tooltips.
// =============================================================

import React, { createContext, useContext, useState, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';

// --- CONTEXT DEFINITION ---
interface HelpContextType {
  isHelpEnabled: boolean;
  toggleHelp: () => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

// --- PROVIDER COMPONENT ---
export function HelpProvider({ children }: { children: React.ReactNode }) {
  const [isHelpEnabled, setIsHelpEnabled] = useState(() => {
    // Load preference from local storage on init, default to true
    try {
      const saved = localStorage.getItem('variance_help_enabled');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const toggleHelp = () => {
    setIsHelpEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem('variance_help_enabled', JSON.stringify(newValue));
      return newValue;
    });
  };

  return (
    <HelpContext.Provider value={{ isHelpEnabled, toggleHelp }}>
      {children}
    </HelpContext.Provider>
  );
}

// --- HOOK TO USE THE CONTEXT ---
export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) throw new Error('useHelp must be used within a HelpProvider');
  return context;
}

// --- FIELD LEVEL TOOLTIP ---
// Small (?) icon that reveals a tooltip on hover. 
// Only renders when Help System is enabled in settings.
export function HelpTooltip({ text }: { text: string }) {
  const { isHelpEnabled } = useHelp();
  if (!isHelpEnabled) return null;

  return (
    <div className="group relative inline-block ml-1 cursor-help align-middle">
      <HelpCircle size={14} className="text-gray-400 hover:text-[var(--brand-accent)] transition-colors" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2 bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-xs text-gray-300 rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none leading-relaxed">
        {text}
      </div>
    </div>
  );
}


/* -------------------------- START ==============
// --- PAGE LEVEL HELP BANNER (OLD)---Disbled ====
// Large banner at the top of pages to explain the page purpose.
// Can be dismissed locally per-page-per-session.
export function PageHelp({ title, description }: { title: string, description: string }) {
  const { isHelpEnabled } = useHelp();
  const [isDismissed, setIsDismissed] = useState(false);

  if (!isHelpEnabled || isDismissed) return null;

  return (
    <div className="p-4 mb-6 rounded-xl border border-blue-500/30 bg-blue-500/10 flex items-start gap-4">
      <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400 mt-0.5">
        <HelpCircle size={20} />
      </div>
      <div className="flex-1">
        <h3 className="text-sm font-bold text-blue-400 mb-1">{title}</h3>
        <p className="text-sm text-blue-100/80 leading-relaxed">{description}</p>
      </div>
      <button onClick={() => setIsDismissed(true)} className="text-blue-400/50 hover:text-blue-400 transition-colors">
        <X size={18} />
      </button>
    </div>
  );
}
======================================= END ===== */

// --- PAGE LEVEL HELP BANNER (Advanced) ---
// Large banner at the top of pages to explain the page purpose.
// Can be dismissed locally per-page-per-session.
export function PageHelp({ title, description }: { title: string, description: string }) {
  const { isHelpEnabled } = useHelp();
  const [isDismissed, setIsDismissed] = useState(false);

  if (!isHelpEnabled || isDismissed) return null;

  return (
    <div className="p-4 mb-6 rounded-xl border flex items-start gap-4 transition-all" 
         style={{ 
           backgroundColor: 'var(--bg-panel)', 
           borderColor: 'var(--border-subtle)',
           boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.2)'
         }}>
      
      {/* Icon with subtle brand accent background */}
      <div className="p-2 rounded-lg mt-0.5" 
           style={{ backgroundColor: 'var(--bg-panel-alt)', color: 'var(--brand-accent)' }}>
        <HelpCircle size={20} />
      </div>
      
      <div className="flex-1">
        <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--brand-accent)' }}>
          {title}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          {description}
        </p>
      </div>
      
      <button 
        onClick={() => setIsDismissed(true)} 
        className="transition-colors hover:text-white"
        style={{ color: 'var(--text-secondary)' }}
      >
        <X size={18} />
      </button>
    </div>
  );
}

